/**
 * Toolbar of the Home page: the tab bar, the loading spinner, the search field
 * and the Workflow menu. It is one row on desktop; below the width of the tabs
 * plus the controls, the right group wraps whole onto a second line. Rendered by `HomePageContent`; every
 * child but the Workflow menu reads the orchestrator, and the Workflow menu
 * only creates forms. Goal progress lives in the Home tiles, not here.
 * `TableFilterButtons` is the row's one growing element, so the view menu lives
 * inside its tab `Menu`, not beside it where it would ride the bar's right edge.
 * The spinner, search field and Workflow menu form one non-growing, non-wrapping
 * group that holds the row's right edge once the capped tab bar leaves free
 * width; on a phone the search field shrinks inside it.
 */
import { FC, type RefObject } from 'react';
import { Spin, type InputRef } from 'antd';
import { useSnapshot } from 'valtio';
import FlexBox from '../../../atoms/FlexBox';
import { useFormTypes } from '../../../../use/useFormTypes';
import TableFilterButtons, {
  type TableFilterViewId,
} from './TableFilterButtons';
import HomePageWorkflowMenu from './HomePageWorkflowMenu';
import HomePageSearchBar from './HomePageSearchBar';
import { homePageConfig } from '#types';
import type { HomePageOrchestrator } from '../HomePageContent/useHomePageOrchestrator';

export type HomePageToolbarViewType = TableFilterViewId;

interface HomePageToolbarProps {
  orchestrator: HomePageOrchestrator;
  searchInputRef: RefObject<InputRef | null>;
}

const HomePageToolbar: FC<HomePageToolbarProps> = ({
  orchestrator: o,
  searchInputRef,
}) => {
  const formTypes = useFormTypes();
  const snap = useSnapshot(o.store);

  const reports = homePageConfig.formsTable.reports.map((report) => ({
    id: report.id,
    label: report.label,
  }));

  return (
    <FlexBox noGrow wrap style={{ width: '100%', minWidth: 0 }} gap={15}>
      <TableFilterButtons
        orchestrator={o}
        activeViewId={o.activeView}
        reports={reports}
      />
      <FlexBox noGrow justifyEnd style={{ minWidth: 0 }} gap={15}>
        <Spin
          spinning={snap.isLoading}
          style={{
            minWidth: 30,
          }}
        />
        <HomePageSearchBar
          value={o.searchQuery}
          onChange={o.setSearchQuery}
          inputRef={searchInputRef}
        />
        <HomePageWorkflowMenu formTypes={formTypes} />
      </FlexBox>
    </FlexBox>
  );
};

export default HomePageToolbar;
